import { type ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { AUTH_PATHS } from "../../../constants";
import { useAuthLinkConfirmation } from "../../../hooks/auth/useAuthLinkConfirmation";
import { AuthLayout } from "../AuthLayout";
import { type AuthLinkConfirmVariant, AuthLinkConfirmView } from "./AuthLinkConfirmView";

interface AuthLinkConfirmProps {
  /** Which copy the View shows while the gate is active. */
  variant: AuthLinkConfirmVariant;
  /**
   * Fired exactly once, on the first render where `status` is "success" — which may be
   * the mount render, if this link was already confirmed earlier in the tab.
   */
  onConfirmed?: () => void;
  /**
   * Fired exactly once when there is no link to confirm — e.g. AuthCallback's redirect
   * back to sign-in. AuthUpdate passes none: at /auth/update it sits behind ProtectedRoute,
   * so a link-less unauthenticated visit is already redirected before it renders.
   */
  onNoLink?: () => void;
  /**
   * Rendered whenever the gate is not active: no link, after a successful confirm, or for
   * the one render before an error redirects to sign-in.
   */
  children?: ReactNode;
}

/**
 * Single owner of the link-confirm flow shared by AuthCallback and AuthUpdate: calls
 * `useAuthLinkConfirmation()`, redirects to sign-in with the failure message on error,
 * notifies the caller once on success (or once on no-link), and renders the confirm
 * button while idle/confirming.
 */
export const AuthLinkConfirm = ({
  variant,
  onConfirmed,
  onNoLink,
  children,
}: AuthLinkConfirmProps) => {
  const navigate = useNavigate();
  const authLinkConfirmation = useAuthLinkConfirmation();
  const { linkPresent, status, confirm } = authLinkConfirmation;
  const errorMessage = status === "error" ? authLinkConfirmation.errorMessage : undefined;
  const hasConfirmedNotifiedRef = useRef(false);
  const hasNoLinkNotifiedRef = useRef(false);

  useEffect(() => {
    if (status === "error") {
      navigate(AUTH_PATHS.SIGNIN, {
        replace: true,
        state: { errorMessage },
      });
    }
  }, [status, errorMessage, navigate]);

  useEffect(() => {
    if (status === "success" && !hasConfirmedNotifiedRef.current) {
      hasConfirmedNotifiedRef.current = true;
      onConfirmed?.();
    }
  }, [status, onConfirmed]);

  useEffect(() => {
    if (!linkPresent && !hasNoLinkNotifiedRef.current) {
      hasNoLinkNotifiedRef.current = true;
      onNoLink?.();
    }
  }, [linkPresent, onNoLink]);

  const isGateActive = linkPresent && (status === "idle" || status === "confirming");

  if (isGateActive) {
    return (
      <AuthLayout>
        <AuthLinkConfirmView
          variant={variant}
          isConfirming={status === "confirming"}
          onConfirm={confirm}
        />
      </AuthLayout>
    );
  }

  return <>{children}</>;
};
